import { useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { Maximize2, Minimize2, X } from 'lucide-react';
import {
  AssistantChat,
  AssistantHeader,
  ASSISTANT_LAUNCHER_ID,
  ASSISTANT_PANEL_ID,
} from '@wianco/portal-assistant';
import { useNavigationStore } from '@/features/navigation/store';
import { useShellI18n } from '@/features/language/useI18n';
import { useAssistantPanelResize } from './useAssistantPanelResize';
import { useAssistantViewport } from './useAssistantViewport';
import {
  ASSISTANT_PANEL_MIN_WIDTH,
  getAssistantPanelPresetToggle,
  isAssistantPanelWide,
} from './panelWidth';
import styles from './AssistantDock.module.css';

/** Shell owns the dock geometry; the content and provider belong to Assistant. */
export function AssistantDock() {
  const open = useNavigationStore((state) => state.assistantOpen);
  const width = useNavigationStore((state) => state.assistantPanelWidth);
  const close = useNavigationStore((state) => state.closeAssistant);
  const setWidth = useNavigationStore((state) => state.setAssistantPanelWidth);
  const panelRef = useRef<HTMLElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const { maxWidth, handleProps, isMobile } = useAssistantPanelResize(panelRef, handleRef);
  useAssistantViewport(panelRef, open && isMobile);
  useEffect(() => {
    if (!open || !isMobile) return;
    const siblings = Array.from(panelRef.current?.parentElement?.children ?? []).filter(
      (element) => element !== panelRef.current && !element.hasAttribute('inert')
    );
    siblings.forEach((element) => element.setAttribute('inert', ''));
    // Focus the dialog for keyboard/screen-reader navigation without opening
    // a phone keyboard. The composer is focused only when the user taps it.
    panelRef.current?.focus({ preventScroll: true });
    return () => siblings.forEach((element) => element.removeAttribute('inert'));
  }, [open, isMobile]);
  const { t } = useShellI18n();
  const wide = isAssistantPanelWide(width, window.innerWidth);
  const closeAndFocus = () => {
    close();
    if (isMobile) {
      // Wait for modal cleanup to make the underlying page interactive again.
      window.requestAnimationFrame(() => document.getElementById('mobile-menu-button')?.focus());
    } else {
      document.getElementById(ASSISTANT_LAUNCHER_ID)?.focus();
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      closeAndFocus();
    }
    // Keep keyboard focus inside the mobile sheet while the underlying page is covered.
    if (event.key === 'Tab' && open && window.innerWidth <= 980) {
      const controls = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), textarea:not(:disabled), input:not(:disabled), [tabindex="0"]'
        ) ?? []
      ).filter((element) => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };
  return (
    <aside
      ref={panelRef}
      id={ASSISTANT_PANEL_ID}
      className={styles.panel}
      aria-label="WIANCO AI"
      role={isMobile ? 'dialog' : undefined}
      tabIndex={-1}
      aria-modal={isMobile && open ? true : undefined}
      aria-hidden={!open}
      data-state={open ? 'open' : 'closed'}
      style={{ '--assistant-panel-width': `${width}px` } as CSSProperties}
      onKeyDown={onKeyDown}
    >
      <AssistantHeader>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.widenButton}`}
          aria-label={t(wide ? 'Narrow assistant panel' : 'Widen assistant panel')}
          onClick={() => setWidth(getAssistantPanelPresetToggle(width, window.innerWidth))}
        >
          {wide ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={closeAndFocus}
          aria-label={t('Close WIANCO AI')}
        >
          <X aria-hidden="true" />
        </button>
      </AssistantHeader>
      <div className={styles.mount}>
        <AssistantChat open={open} />
      </div>
      <div
        ref={handleRef}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('Resize assistant panel')}
        aria-controls={ASSISTANT_PANEL_ID}
        aria-valuenow={width}
        aria-valuemin={ASSISTANT_PANEL_MIN_WIDTH}
        aria-valuemax={maxWidth}
        tabIndex={0}
        className={styles.handle}
        {...handleProps}
      />
    </aside>
  );
}
